import { Field, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { useCurrencies } from '../../../lib/reference/currencies.js';
import { type ExchangeRateFormInput } from '../api/schemas.js';

export function ExchangeRateFields({
  register,
  errors,
  control,
}: {
  readonly register: UseFormRegister<ExchangeRateFormInput>;
  readonly errors: FieldErrors<ExchangeRateFormInput>;
  readonly control: Control<ExchangeRateFormInput>;
}): React.JSX.Element {
  const currenciesQuery = useCurrencies();

  const currencyOptions =
    currenciesQuery.data?.map((currency) => ({
      value: currency.code,
      label: `${currency.code} — ${currency.name}`,
    })) ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Controller
          control={control}
          name="baseCurrency"
          render={({ field, fieldState }) => (
            <Field error={fieldState.error?.message ?? errors.baseCurrency?.message} required>
              <FieldLabel>Base currency</FieldLabel>
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                options={currencyOptions}
                placeholder="Choose the base currency"
                disabled={currenciesQuery.isPending}
                searchable
              />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="quoteCurrency"
          render={({ field, fieldState }) => (
            <Field error={fieldState.error?.message ?? errors.quoteCurrency?.message} required>
              <FieldLabel>Quote currency</FieldLabel>
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                options={currencyOptions}
                placeholder="Choose the quote currency"
                disabled={currenciesQuery.isPending}
                searchable
              />
            </Field>
          )}
        />
      </div>

      <Field error={errors.rate?.message} required>
        <FieldLabel>Rate</FieldLabel>
        <FieldInput
          autoComplete="off"
          placeholder="1.2345"
          type="text"
          inputMode="decimal"
          {...register('rate')}
        />
      </Field>

      <Field error={errors.source?.message} required>
        <FieldLabel>Source</FieldLabel>
        <FieldInput autoComplete="off" placeholder="Manual or provider" {...register('source')} />
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.effectiveAt?.message}>
          <FieldLabel>Effective at</FieldLabel>
          <FieldInput type="date" {...register('effectiveAt')} />
        </Field>

        <Field error={errors.expiresAt?.message}>
          <FieldLabel>Expires at</FieldLabel>
          <FieldInput type="date" {...register('expiresAt')} />
        </Field>
      </div>
    </div>
  );
}
